import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { Alert, Button, Heading, Box, List, ListItem } from '@razorpay/blade/components';
import api from 'api';
import { useFormWithProvider } from 'forms';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import { AppError } from 'utils/AppError';
import { parseGetSalaryStructureResponse } from 'components/SalaryStructure/utils';
import { SalaryStructureFormData } from 'components/SalaryStructures/creation/types';
import { SalaryStructureCreationFlow } from 'components/SalaryStructures/creation';
import { routePaths } from 'components/Routes/data';
import { parseError } from 'utils/Api';
import { SalaryStrctureComponents } from './SalaryStrctureComponents';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';

export const RearchSalaryStructureForm = () => {
  const { setFieldValue } = useFormWithProvider<SalaryStructureFormData>();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const queryClient = useQueryClient();
  const closeEdit = () => {
    setIsEditOpen(false);
    queryClient.invalidateQueries([salaryStructureQueries.getSalaryStructure]);
  };
  const salaryStructureQueryResponse = useQuery({
    queryKey: [salaryStructureQueries.getSalaryStructure],
    queryFn: () =>
      api.salaryStructures.queries.getSalaryStructure({
        input: { type: 'SALARY_STRUCTURE_TYPE_DEFAULT' },
      }),
    select: (data) => {
      const response = parseGetSalaryStructureResponse(data);
      const salaryComponents =
        response.payrollGetSalaryStructure.data?.salaryStructureComponents || [];
      const sortedComponents = [...salaryComponents].sort((component1, component2) => {
        if (component1.salaryComponent.name === 'Basic') return -1;
        if (component2.salaryComponent.name === 'Basic') return 1;

        // Special Allowance comes last
        if (component1.salaryComponent.name === 'Special Allowance') return 1;
        if (component2.salaryComponent.name === 'Special Allowance') return -1;

        return 0;
      });
      return {
        ...response.payrollGetSalaryStructure.data,
        salaryStructureComponents: sortedComponents,
      };
    },
    onError: (e: AppError) => {},
    onSuccess: (data) => {
      setFieldValue('templateId', [data.id]);
    },
  });

  const completeSalaryStructureMutation = useMutation({
    mutationFn: () => api.companyOnboarding.completeSalaryStructureSetup(),
    onSuccess: () => {
      window.location.href = routePaths.phpRoutes.dashboard;
    },
    onError: (e: typeof AppError) => {},
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    completeSalaryStructureMutation.mutate();
  };

  const { isFetching: isFetchingSalaryStructure } = salaryStructureQueryResponse;

  return (
    <ResetLocalOverridingStyles>
      <Box display="flex" alignItems="center" justifyContent="center">
        <form onSubmit={handleSubmit}>
          {completeSalaryStructureMutation.status === 'error' &&
            completeSalaryStructureMutation.error && (
              <Alert
                color="negative"
                description={parseError(completeSalaryStructureMutation.error)}
              />
            )}
          <Heading weight="regular" color="surface.text.gray.normal" size="small">
            Default Salary Structure
          </Heading>
          <Box
            borderWidth="thin"
            borderColor="surface.border.gray.subtle"
            backgroundColor="surface.background.gray.intense"
            borderRadius="large"
            padding={['spacing.7', 'spacing.7', 'spacing.5']}
            marginTop="spacing.3"
            marginBottom="spacing.7">
            <List variant="ordered" size="medium">
              <ListItem>
                You can view the default salary structure that XPayroll uses for your employees.
              </ListItem>
              <ListItem>
                Employer contributions to PF and ESI (if applicable) are applied after the salary
                has been bifurcated into the components in the ratio defined. Hence, the final
                salary structure can have a slightly different ratio.
              </ListItem>
            </List>
          </Box>

          <SalaryStrctureComponents
            salaryStructureQueryResponse={salaryStructureQueryResponse}
            openModifyStructureModal={() => setIsEditOpen(true)}
          />

          <Button
            type="submit"
            isLoading={isFetchingSalaryStructure || completeSalaryStructureMutation.isLoading}>
            Continue
          </Button>
        </form>

        <SalaryStructureCreationFlow isOpen={isEditOpen} onClose={closeEdit} initialStep={1} />
      </Box>
    </ResetLocalOverridingStyles>
  );
};
